import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DataLayerService } from '../data-layer/data-layer.service';
import { Command } from 'src/app/models/Command';
import { FeatureFlag, FeatureEnvironment, ENV_KEY_MAP } from 'src/app/models/FeatureFlagModel';

// Server-driven feature flags for the offline-first PWA.
//
// - Flags are loaded once per session from the backend (`cmdFeaturesNav`) and
//   cached in localStorage so isEnabled() keeps working after an offline reload.
// - The current environment (environment.env) selects which per-env boolean wins.
// - isEnabled() is synchronous, so it can be called from templates, components,
//   and route logic without awaiting anything.
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {

  private static readonly STORAGE_KEY = 'featureFlags';
  private readonly currentEnv: FeatureEnvironment = environment.env;

  private flags: FeatureFlag[] = [];

  constructor(private dl: DataLayerService) {
    this.hydrateFromCache();
  }

  // Restore the last-known flags so offline / pre-refresh checks have an answer.
  private hydrateFromCache(): void {
    try {
      const raw = localStorage.getItem(FeatureFlagService.STORAGE_KEY);
      if (raw) {
        this.flags = JSON.parse(raw);
      }
    } catch (e) {
      console.warn('[FeatureFlagService] could not parse cached flags', e);
      this.flags = [];
    }
  }

  private normalise(raw: any): FeatureFlag {
    return {
      code:        raw.code,
      development: Boolean(raw.development),
      alpha:       Boolean(raw.alpha),
      beta:        Boolean(raw.beta),
      production:  Boolean(raw.production),
    };
  }

  // Is a feature code enabled for the current environment?
  // Unknown code → false (fail closed).
  isEnabled(code: string): boolean {
    const flag = this.flags.find(f => f.code === code);
    if (!flag) {
      return false;
    }
    return flag[ENV_KEY_MAP[this.currentEnv]];
  }

  // Pull fresh flags from the backend. Call at boot (once a session exists) and
  // after login. When offline, keep whatever is already cached.
  async loadFlags(): Promise<void> {
    if (!this.dl.online) {
      return;
    }

    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesNav';
    cmd.addParameter('Environment', this.currentEnv);

    try {
      const res: any = await this.dl.command(cmd).toPromise();
      const list: any[] = Array.isArray(res) ? res : (res?.recordset ?? res?.data ?? []);
      this.flags = (list ?? []).map(f => this.normalise(f));
      localStorage.setItem(FeatureFlagService.STORAGE_KEY, JSON.stringify(this.flags));
    } catch (err) {
      // Keep the cached flags on failure rather than blanking everything out.
      console.warn('[FeatureFlagService] cmdFeaturesNav failed — keeping cached flags.', err);
    }
  }
}
